import React, { useState, useEffect, useMemo } from 'react';
import { Scatter } from 'react-chartjs-2';
import { Chart as ChartJS, LinearScale, PointElement, LineElement, Tooltip, Legend } from 'chart.js';
import { useDarkMode } from '../../../DarkModeContext';
import Loading from '../../../components/loading';
import { useNavigate, useParams } from 'react-router-dom';

ChartJS.register(LinearScale, PointElement, LineElement, Tooltip, Legend);

const lowImpactAreaPlugin = {
  id: 'lowImpactArea',
  beforeDraw: (chart) => {
    const ctx = chart.ctx;
    const xAxis = chart.scales.x;
    const yAxis = chart.scales.y;
    const origin = xAxis.getPixelForValue(0);    
    const yorigin = yAxis.getPixelForValue(0);
    const chartRight = chart.chartArea.right;
    const chartTop = chart.chartArea.top;
    const xYellowThreshold = xAxis.getPixelForValue(70);
    const yYellowThreshold = yAxis.getPixelForValue(4000);
    
    // Draw red background for the entire chart area
    ctx.save();
    ctx.fillStyle = 'rgba(255, 0, 0, 0.5)';
    ctx.fillRect(origin, chartTop, chartRight - origin, yorigin - chartTop);
    ctx.restore();
    
    // Draw yellow area on top
    ctx.save();
    ctx.fillStyle = 'rgba(255, 255, 0, 0.5)';
    ctx.fillRect(origin, yorigin, xYellowThreshold - origin, yYellowThreshold - yorigin);
    ctx.restore();
  }
};

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    console.error("Caught an error:", error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return <h1>Something went wrong.</h1>;
    }

    return this.props.children;
  }
}

const Analytics = () => {
  const [playerData, setPlayerData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionType, setSessionType] = useState('allTime');
  const { isDarkMode } = useDarkMode();
  const navigate = useNavigate();
  const { playerId } = useParams();

  const fetchData = async (url) => {
    console.log('Fetching data from:', url);
    const username = 'TestEnvAdmin';
    const password = 'qwerty@123';

    const headers = new Headers();
    headers.set('Authorization', 'Basic ' + btoa(username + ":" + password));

    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: headers,
        credentials: 'include',
        signal: AbortSignal.timeout(60000)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('API Response:', data);

      setPlayerData(data);
    } catch (e) {
      console.error('Error in fetchData:', e);
      setError(`Failed to fetch player data: ${e.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (playerId) {
      setLoading(true);
      setError(null);
      console.log('Session type changed to:', sessionType);
      if (sessionType === 'allTime') {
        fetchData(`https://staging-sit.com/api/player-session-impacts/player_impacts/?player_id=${playerId}`);
      } else if (sessionType === 'lastSession') {
        fetchData(`https://staging-sit.com/api/session-data/${playerId}/latest-session/`);
      }
    }
  }, [playerId, sessionType]);

  const handleGoBack = () => {
    navigate('/admin/player-analytics');
  };

  const handleSessionTypeChange = (type) => {
    console.log('Changing session type to:', type);
    setSessionType(type);
  };

  const chartData = useMemo(() => {
    console.log('Preparing chart data. Player data:', playerData);
    if (!playerData) {
      return { datasets: [{ data: [] }] };
    }

    let impacts = [];
    if (sessionType === 'lastSession' && Array.isArray(playerData.impacts)) {
      impacts = playerData.impacts;
    } else if (sessionType === 'allTime' && Array.isArray(playerData.impacts)) {
      impacts = playerData.impacts;
    } else {
      console.error('Unexpected data structure:', playerData);
      return { datasets: [{ data: [] }] };
    }

    console.log('Number of impacts:', impacts.length);

    return {
      datasets: [{
        label: 'Player Impacts',
        data: impacts.map(item => ({
          x: item.linear_impact,
          y: item.rotational_impact,
          time: item.time,
          linearAcc: item.linear_impact,
          rotAcc: item.rotational_impact
        })),
        backgroundColor: 'rgba(90, 34, 139, 0.6)',
        borderColor: 'white',
        borderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
      }],
    };
  }, [playerData, sessionType]);

  const options = useMemo(() => {
    const xMax = Math.max(...chartData.datasets[0].data.map(d => d.x), 140);
    const yMax = Math.max(...chartData.datasets[0].data.map(d => d.y), 8000);

    return {
      scales: {
        x: {
          type: 'linear',
          position: 'bottom',
          title: {
            display: true,
            text: 'Linear Impact (g)',
            color: isDarkMode ? 'white' : 'black'
          },
          min: 0,
          max: xMax > 140 ? xMax * 1.1 : 140,
          ticks: {
            color: isDarkMode ? 'white' : 'black'
          },
          grid: {
            color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
          }
        },
        y: {
          type: 'linear',
          position: 'left',
          title: {
            display: true,
            text: 'Rotational Impact (rad/s²)',
            color: isDarkMode ? 'white' : 'black'
          },
          min: 0,
          max: yMax > 8000 ? yMax * 1.1 : 8000,
          ticks: {
            color: isDarkMode ? 'white' : 'black'
          },
          grid: {
            color: isDarkMode ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
          }
        },
      },
      plugins: {
        tooltip: {
          callbacks: {
            label: (context) => {
              const point = context.raw;
              return [
                `Time: ${new Date(point.time).toLocaleString()}`,
                `Linear Acceleration: ${point.linearAcc.toFixed(2)} g`,
                `Rotational Acceleration: ${point.rotAcc.toFixed(2)} rad/s²`
              ];
            },
            title: () => null,
          },
          itemSort: (a, b) => b.raw.y - a.raw.y,
          filter: (tooltipItem, index, tooltipItems) => index === 0,
          backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.8)' : 'rgba(255, 255, 255, 0.8)',
          titleColor: isDarkMode ? 'white' : 'black',
          bodyColor: isDarkMode ? 'white' : 'black',
        },
        legend: {
          display: false
        },
      },
      responsive: true,
      maintainAspectRatio: false
    };
  }, [chartData, isDarkMode]);

  if (loading) return <div className={isDarkMode ? "text-white" : ""}><Loading/></div>;
  if (error) return <div className={isDarkMode ? "text-white" : ""}>Error: {error}</div>;

  return (
    <ErrorBoundary>
      <div className={`w-full ${isDarkMode ? 'bg-[#252540]' : ''} p-4`}>
        <div className="flex justify-between items-center mb-4">
          <button 
            onClick={handleGoBack}
            className="bg-[#f46524] text-white px-4 py-2 rounded hover:bg-[#f46570] transition-colors duration-200"
          >
            GO BACK
          </button>
        </div>
        <div className="flex justify-between items-center mb-4">
          <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : ''}`}>
            Player Impact Analysis: {playerData?.player_name || 'Unknown Player'}
          </h2>
          <div className="flex space-x-2">
            <button
              onClick={() => handleSessionTypeChange('allTime')}
              className={`px-4 py-2 rounded ${sessionType === 'allTime' ? 'bg-[#f46524] text-white' : 'bg-gray-200 text-gray-800'}`}
            >
              All Time
            </button>
            <button
              onClick={() => handleSessionTypeChange('lastSession')}
              className={`px-4 py-2 rounded ${sessionType === 'lastSession' ? 'bg-[#f46524] text-white' : 'bg-gray-200 text-gray-800'}`}
            >
              Last Session
            </button>
            <button
              onClick={() => handleSessionTypeChange('custom')}
              className={`px-4 py-2 rounded ${sessionType === 'custom' ? 'bg-[#f46524] text-white' : 'bg-gray-200 text-gray-800'}`}
            >
              Custom
            </button>
          </div>
        </div>
        
        <div className={`${isDarkMode ? 'bg-[#303050]' : 'bg-white'} p-4 rounded-lg shadow-lg`} style={{ height: '500px' }}>
          {chartData.datasets[0].data.length > 0 ? (
            <Scatter data={chartData} options={options} plugins={[lowImpactAreaPlugin]} />
          ) : (
            <div className={`text-center ${isDarkMode ? 'text-white' : ''}`}>No impact data available for this player.</div>
          )}
        </div>

        {sessionType === 'lastSession' && playerData?.latest_session && (
          <div className={`mt-4 ${isDarkMode ? 'text-white' : 'text-black'}`}>
            <h3 className="text-lg font-semibold">Last Session Details:</h3>
            <p>Date: {new Date(playerData.latest_session.date).toLocaleString()}</p>
            <p>Max Linear Impact: {playerData.latest_session.max_lin_impact} g</p>
            <p>Max Rotational Impact: {playerData.latest_session.max_rot_impact} rad/s²</p>
            <p>Average Linear Impact: {playerData.latest_session.avg_lin_impact.toFixed(2)} g</p>
            <p>Average Rotational Impact: {playerData.latest_session.avg_rot_impact.toFixed(2)} rad/s²</p>
            <p>Cumulative Linear Impact: {playerData.latest_session.cumulative_lin_impact.toFixed(2)} g</p>
            <p>Cumulative Rotational Impact: {playerData.latest_session.cumulative_rot_impact.toFixed(2)} rad/s²</p>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default Analytics;